<template>
  <div class="product-pricing-section panel-card">
    <div class="section-head">
      <div>
        <h4><i class="fas fa-tags me-2"></i>Precios y presentaciones</h4>
        <p class="section-desc">
          Definí stock y precio por rol. El rol por defecto usa el precio base del producto.
        </p>
      </div>
    </div>

    <!-- ¿Tiene variantes? (solo semillas) -->
    <div v-if="!isMerch" class="variant-toggle">
      <span class="toggle-label">¿Este producto tiene variantes?</span>
      <div class="toggle-options">
        <button
          type="button"
          class="toggle-btn"
          :class="{ active: hasVariants }"
          @click="setHasVariants(true)"
        >
          <i class="fas fa-layer-group me-1"></i>Sí, varias presentaciones
        </button>
        <button
          type="button"
          class="toggle-btn"
          :class="{ active: !hasVariants }"
          @click="setHasVariants(false)"
        >
          <i class="fas fa-box me-1"></i>No, una sola presentación
        </button>
      </div>
    </div>

    <div v-if="loadingRoles" class="text-center py-4">
      <div class="spinner-border spinner-border-sm text-primary"></div>
      <span class="ms-2 text-muted-custom">Cargando roles…</span>
    </div>

    <template v-else>
      <div
        v-for="(variant, vIndex) in localVariants"
        :key="vIndex"
        class="variant-card"
      >
        <div class="variant-card-head">
          <span class="variant-title">
            <i class="fas fa-cube me-2"></i>
            {{ isMerch ? 'Presentación única' : `Presentación ${vIndex + 1}` }}
          </span>
          <button
            v-if="!isMerch && hasVariants && localVariants.length > 1"
            type="button"
            class="btn btn-sm btn-outline-danger"
            @click="removeVariant(vIndex)"
          >
            <i class="fas fa-trash"></i>
          </button>
        </div>

        <div class="row g-3 mb-3">
          <div v-if="!isMerch" class="col-md-3">
            <label class="form-label">Semillas por blister</label>
            <input
              v-model.number="variant.seed_count"
              type="number"
              min="1"
              class="form-control"
              required
            />
          </div>
          <div class="col-md-3">
            <label class="form-label">Stock</label>
            <input
              v-model.number="variant.stock"
              type="number"
              min="0"
              class="form-control"
              required
            />
          </div>
          <div class="col-md-3">
            <label class="form-label">Descuento (%)</label>
            <input
              v-model.number="variant.discount"
              type="number"
              min="0"
              max="100"
              class="form-control"
            />
          </div>
        </div>

        <div class="role-prices-block">
          <div class="role-prices-head">
            <span><i class="fas fa-id-badge me-1"></i>Precio por rol</span>
            <small class="text-muted-custom">Dejá vacío para usar el precio del rol por defecto</small>
          </div>
          <div class="role-prices-grid">
            <div
              v-for="role in customerRoles"
              :key="role.slug"
              class="role-price-row"
              :class="{ 'is-default': role.is_default }"
            >
              <span
                class="role-tag"
                :style="{ backgroundColor: role.color || '#adb5bd' }"
              >{{ role.label }}</span>
              <div class="role-price-input-wrap">
                <span class="currency">$</span>
                <input
                  v-model.number="variant.role_prices[role.slug]"
                  type="number"
                  min="0"
                  step="0.01"
                  class="form-control role-price-input"
                  :placeholder="role.is_default ? 'Precio base' : 'Opcional'"
                  :required="role.is_default"
                />
              </div>
              <span v-if="role.is_default" class="default-badge">Base</span>
            </div>
          </div>
          <p v-if="defaultRoleSlug && variant.role_prices[defaultRoleSlug]" class="preview-price">
            Precio final ({{ defaultRoleLabel }}):
            <strong>${{ formatPreview(variant, defaultRoleSlug) }}</strong>
            <span v-if="variant.discount > 0" class="text-muted-custom ms-1">
              (con {{ variant.discount }}% dto → ${{ formatWithDiscount(variant, defaultRoleSlug) }})
            </span>
          </p>
        </div>
      </div>

      <button
        v-if="!isMerch && hasVariants"
        type="button"
        class="btn btn-outline-primary btn-sm add-variant-btn"
        @click="addVariant"
      >
        <i class="fas fa-plus me-1"></i>Agregar presentación
      </button>
    </template>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { getCustomerRoles } from '@/services/invitations'

function payloadsEqual(a, b) {
  return JSON.stringify(a ?? []) === JSON.stringify(b ?? [])
}

export default {
  name: 'ProductPricingSection',
  props: {
    blisters: { type: Array, default: () => [] },
    isMerch: { type: Boolean, default: false },
  },
  emits: ['update:blisters'],
  setup(props, { emit }) {
    const customerRoles = ref([])
    const loadingRoles = ref(true)
    const hasVariants = ref(true)
    const localVariants = ref([])
    const syncing = ref(false)

    const defaultRole = computed(() => customerRoles.value.find(r => r.is_default) || customerRoles.value[0])
    const defaultRoleSlug = computed(() => defaultRole.value?.slug || 'retail')
    const defaultRoleLabel = computed(() => defaultRole.value?.label || 'Cliente')

    const emptyVariant = (seedCount = 3) => ({
      seed_count: props.isMerch ? 1 : seedCount,
      stock: 0,
      discount: 0,
      price: 0,
      role_prices: {},
    })

    const initRolePrices = (existing = {}) => {
      const rp = { ...existing }
      customerRoles.value.forEach((role) => {
        if (rp[role.slug] === undefined) {
          rp[role.slug] = ''
        }
      })
      return rp
    }

    const buildPayload = () => {
      return localVariants.value.map((v) => {
        const role_prices = { ...v.role_prices }
        const baseSlug = defaultRoleSlug.value
        const basePrice = Number(role_prices[baseSlug] ?? v.price ?? 0)
        if (baseSlug) role_prices[baseSlug] = basePrice
        return {
          seed_count: props.isMerch ? 1 : Number(v.seed_count),
          stock: Number(v.stock),
          discount: Number(v.discount || 0),
          price: basePrice,
          role_prices,
        }
      })
    }

    const normalizeFromProps = async () => {
      syncing.value = true
      const source = props.blisters?.length ? props.blisters : [emptyVariant()]

      if (!props.isMerch && source.length === 1) {
        hasVariants.value = false
      } else if (!props.isMerch) {
        hasVariants.value = true
      }

      localVariants.value = source.map((b) => ({
        seed_count: b.seed_count ?? (props.isMerch ? 1 : 3),
        stock: b.stock ?? 0,
        discount: b.discount ?? 0,
        price: b.price ?? 0,
        role_prices: initRolePrices({
          ...(b.role_prices || {}),
          ...(defaultRoleSlug.value && b.price != null && !b.role_prices?.[defaultRoleSlug.value]
            ? { [defaultRoleSlug.value]: Number(b.price) }
            : {}),
        }),
      }))

      await nextTick()
      syncing.value = false
    }

    const emitUpdate = async () => {
      if (syncing.value) return

      const payload = buildPayload()
      if (payloadsEqual(payload, props.blisters)) return

      syncing.value = true
      emit('update:blisters', payload)
      await nextTick()
      syncing.value = false
    }

    watch(localVariants, () => {
      emitUpdate()
    }, { deep: true })

    watch(
      () => props.blisters,
      (incoming) => {
        if (syncing.value) return
        if (payloadsEqual(buildPayload(), incoming)) return
        normalizeFromProps()
      },
      { deep: true }
    )

    const setHasVariants = (value) => {
      hasVariants.value = value
      if (!value && localVariants.value.length > 1) {
        localVariants.value = [localVariants.value[0]]
      }
      if (value && localVariants.value.length === 0) {
        localVariants.value = [emptyVariant()]
      }
    }

    const addVariant = () => {
      localVariants.value.push(emptyVariant())
    }

    const removeVariant = (index) => {
      if (localVariants.value.length <= 1) return
      localVariants.value.splice(index, 1)
    }

    const formatPreview = (variant, slug) => {
      const p = Number(variant.role_prices[slug] || 0)
      return p.toLocaleString('es-AR')
    }

    const formatWithDiscount = (variant, slug) => {
      const p = Number(variant.role_prices[slug] || 0)
      const d = Number(variant.discount || 0)
      return (p * (1 - d / 100)).toLocaleString('es-AR', { maximumFractionDigits: 0 })
    }

    onMounted(async () => {
      try {
        const data = await getCustomerRoles({ active_only: 'true' })
        customerRoles.value = (Array.isArray(data) ? data : data.results || []).filter(r => r.is_active)
      } catch {
        customerRoles.value = []
      } finally {
        loadingRoles.value = false
        await normalizeFromProps()
        if (!props.blisters?.length) {
          await emitUpdate()
        }
      }
    })

    return {
      customerRoles,
      loadingRoles,
      hasVariants,
      localVariants,
      defaultRoleSlug,
      defaultRoleLabel,
      setHasVariants,
      addVariant,
      removeVariant,
      formatPreview,
      formatWithDiscount,
    }
  },
}
</script>

<style scoped>
.product-pricing-section {
  padding: 1.25rem;
  margin-bottom: 1.25rem;
}

.section-head h4 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.35rem;
}

.section-desc {
  font-size: 0.85rem;
  color: var(--panel-text-muted);
  margin: 0;
}

.variant-toggle {
  margin: 1.25rem 0;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  border: 1px solid var(--panel-border);
}

.toggle-label {
  display: block;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
}

.toggle-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.toggle-btn {
  border: 1px solid var(--panel-border);
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  border-radius: 10px;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s;
}

.toggle-btn.active {
  background: rgba(255, 236, 0, 0.12);
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.variant-card {
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid var(--panel-border);
  border-radius: 14px;
  padding: 1.15rem;
  margin-bottom: 1rem;
}

.variant-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.variant-title {
  font-weight: 600;
  font-size: 0.95rem;
}

.role-prices-block {
  border-top: 1px solid var(--panel-border-muted);
  padding-top: 1rem;
}

.role-prices-head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
}

.role-prices-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.65rem;
}

.role-price-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    "tag badge"
    "input input";
  align-items: center;
  gap: 0.5rem 0.65rem;
  padding: 0.5rem 0.65rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
  border: 1px solid var(--panel-border-muted);
}

.role-price-row.is-default {
  border-color: rgba(255, 236, 0, 0.25);
  background: rgba(255, 236, 0, 0.05);
}

.role-tag {
  grid-area: tag;
  justify-self: start;
  font-size: 0.68rem;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  color: #111;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
}

.role-price-input-wrap {
  grid-area: input;
  position: relative;
  min-width: 0;
}

.currency {
  position: absolute;
  left: 0.55rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--panel-text-muted);
  font-size: 0.85rem;
  z-index: 1;
}

.role-price-input {
  padding-left: 1.35rem;
  background: rgba(0, 0, 0, 0.25);
  border-color: var(--panel-border);
  color: var(--white-color);
  font-size: 0.9rem;
}

.default-badge {
  grid-area: badge;
  justify-self: end;
  font-size: 0.65rem;
  text-transform: uppercase;
  color: var(--primary-color);
  font-weight: 700;
}

.preview-price {
  margin: 1rem 0 0;
  padding: 0.7rem 0.85rem;
  border-left: 3px solid var(--primary-color);
  background: rgba(255, 236, 0, 0.06);
  font-size: 0.9rem;
}

.preview-price strong {
  color: var(--primary-color);
  font-size: 1rem;
}

.add-variant-btn {
  margin-top: 0.25rem;
}

.text-muted-custom {
  color: var(--panel-text-muted);
}

.form-label {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.75);
}
</style>
